import {
  Color,
  Geometry,
  Mesh,
  Pass,
  Plane,
  Post,
  Program,
  RenderTarget,
  Texture,
  Vec2,
} from 'ogl';

import { createRenderer } from './HeroGradient.renderer';
import {
  advectionShader,
  auroraFragment,
  auroraVertex,
  clearShader,
  curlShader,
  divergenceShader,
  gradientSubtractShader,
  pressureShader,
  simFragment,
  simVertex,
  splatShader,
  vorticityShader,
} from './HeroGradient.shaders';
import { ShaderParameters } from './HeroGradient.store';
import {
  CANVAS_SCALE_FACTOR,
  DoubleFBO,
  HeroGradientMessage,
  HeroGradientRenderer,
  InitMessage,
  MouseMessage,
  ResizeMessage,
  Splat,
} from './HeroGradient.types';
import {
  createDoubleFBO,
  getSupportedFormat,
  loadTextureImage,
} from './HeroGradient.utils';

let isReady: boolean = false;
let revealStartTime: number = 0;
let frameAnimationToken: number = 0;
let running: boolean = false;
let renderer: HeroGradientRenderer;
let mesh: Mesh;
let clearProgram: Mesh;
let splatProgram: Mesh;
let advectionProgram: Mesh;
let divergenceProgram: Mesh;
let curlProgram: Mesh;
let vorticityProgram: Mesh;
let pressureProgram: Mesh;
let gradientSubtractProgram: Mesh;
let density: DoubleFBO;
let velocity: DoubleFBO;
let pressure: DoubleFBO;
let divergence: RenderTarget;
let curl: RenderTarget;
let auroraProgram: Program;
let post: Post;
let pass: Pass;
let prevMouse: Vec2;

const splats: Splat[] = [];
const iterations = 1;
const simResolution = 256;
const dyeResolution = 512;
const texelSize = { value: new Vec2(1 / simResolution) };

// mist / dry ice looking:
// let densityDissipation = 0.99;
// let velocityDissipation = 0.999;
// let pressureDissipation = 0.99;
// let radius = 0.2;
// let numOctaves = 5;
// let randomSeed = 33758.5453123;
// let mouseDeltaScaleFactor = 0.5;
// let curlStrength = 20;

// putty / yoghurt:
// let densityDissipation = 1;
// let velocityDissipation = 0.9419;
// let pressureDissipation = 0.7455;
// let radius = 0.3603;
// let numOctaves = 5;
// let randomSeed = 4353.0285;
// let mouseDeltaScaleFactor = 0.4731;
// let curlStrength = 49.096;

// whatever this is:
// todo: move these into the CMS so we can have per-page parameters
let densityDissipation = 0.9749;
let velocityDissipation = 0.97;
let pressureDissipation = 1;
let radius = 0.5;
let numOctaves = 7;
let randomSeed = 43793.0213;
let mouseDeltaScaleFactor = 0.5389;
let curlStrength = 24.8294;

// Function to draw number of interactions onto input render target
function splat({ x, y, dx, dy }: Splat) {
  const gl = renderer.gl;

  splatProgram.program.uniforms.uTarget.value = velocity.read.texture;
  splatProgram.program.uniforms.aspectRatio.value =
    gl.renderer.width / gl.renderer.height;
  splatProgram.program.uniforms.point.value.set(x, y);
  splatProgram.program.uniforms.color.value.set(dx, dy, 1);
  splatProgram.program.uniforms.radius.value = radius / 100;

  gl.renderer.render({
    scene: splatProgram,
    target: velocity.write,
    sort: false,
    update: false,
  });

  velocity.swap();

  splatProgram.program.uniforms.uTarget.value = density.read.texture;

  gl.renderer.render({
    scene: splatProgram,
    target: density.write,
    sort: false,
    update: false,
  });

  density.swap();
}

/**
 * Initialize the renderer & the scene
 *
 * @param param0 InitMessage payload
 */
function init({ canvas, width, height, sampleImageURL }: InitMessage) {
  renderer = createRenderer({
    canvas,
    width,
    height,
  });
  const gl = renderer.gl;
  const geometry = new Plane(gl);
  const texture = new Texture(gl);

  loadTextureImage(texture, sampleImageURL).then(() => {
    isReady = true;
    revealStartTime = performance.now();
    if (running) {
      render(performance.now());
    }
  });

  auroraProgram = new Program(gl, {
    vertex: auroraVertex,
    fragment: auroraFragment,
    uniforms: {
      u_texture: { value: texture },
      u_resolution: { value: [width, height] },
      u_time: { value: 0 },
      u_mouse: { value: [0, 0] },
      u_num_octaves: { value: numOctaves },
      u_random_seed: { value: randomSeed },
    },
  });

  mesh = new Mesh(gl, {
    geometry,
    program: auroraProgram,
  });
  mesh.scale.set(1, 1, 1);
  mesh.rotation.set(0, 0, 0);
  mesh.setParent(renderer.root);

  post = new Post(gl);
  pass = post.addPass({
    fragment: simFragment,
    uniforms: {
      tFluid: { value: null },
      uTime: { value: 0 },
      uReveal: { value: 0 },
    },
  });

  // Setup FBO's and render targets for fluid sim
  let halfFloat, rgba, rg, r;

  if (gl instanceof WebGL2RenderingContext) {
    halfFloat = gl.HALF_FLOAT;
    rgba = getSupportedFormat(gl, gl.RGBA16F, gl.RGBA, halfFloat);
    rg = getSupportedFormat(gl, gl.RG16F, gl.RG, halfFloat);
    r = getSupportedFormat(gl, gl.R16F, gl.RED, halfFloat);
  } else {
    console.warn('[worker]: Not webgl2');
  }

  density = createDoubleFBO(gl, {
    width: dyeResolution,
    height: dyeResolution,
    type: halfFloat,
    format: rgba?.format,
    internalFormat: rgba?.internalFormat,
    minFilter: gl.LINEAR,
    depth: false,
  });

  velocity = createDoubleFBO(gl, {
    width: simResolution,
    height: simResolution,
    type: halfFloat,
    format: rg?.format,
    internalFormat: rg?.internalFormat,
    minFilter: gl.LINEAR,
    depth: false,
  });

  pressure = createDoubleFBO(gl, {
    width: simResolution,
    height: simResolution,
    type: halfFloat,
    format: r?.format,
    internalFormat: r?.internalFormat,
    minFilter: gl.LINEAR,
    depth: false,
  });

  divergence = new RenderTarget(gl, {
    width: simResolution,
    height: simResolution,
    type: halfFloat,
    format: r?.format,
    internalFormat: r?.internalFormat,
    minFilter: gl.NEAREST,
    depth: false,
  });

  curl = new RenderTarget(gl, {
    width: simResolution,
    height: simResolution,
    type: halfFloat,
    format: r?.format,
    internalFormat: r?.internalFormat,
    minFilter: gl.NEAREST,
    depth: false,
  });

  // Variables to be used for the simulation programs
  const triangle = new Geometry(gl, {
    position: {
      size: 2,
      data: new Float32Array([-1, -1, 3, -1, -1, 3]),
    },
    uv: {
      size: 2,
      data: new Float32Array([0, 0, 2, 0, 0, 2]),
    },
  });

  // create the simulation programs
  clearProgram = new Mesh(gl, {
    geometry: triangle,
    program: new Program(gl, {
      vertex: simVertex,
      fragment: clearShader,
      depthTest: false,
      depthWrite: false,
      uniforms: {
        texelSize,
        uTexture: { value: null },
        value: { value: pressureDissipation },
      },
    }),
  });

  splatProgram = new Mesh(gl, {
    geometry: triangle,
    program: new Program(gl, {
      vertex: simVertex,
      fragment: splatShader,
      depthTest: false,
      depthWrite: false,
      uniforms: {
        texelSize,
        uTarget: { value: null },
        aspectRatio: { value: 1 },
        color: { value: new Color() },
        point: { value: new Vec2() },
        radius: { value: radius / 100 },
      },
    }),
  });

  advectionProgram = new Mesh(gl, {
    geometry: triangle,
    program: new Program(gl, {
      vertex: simVertex,
      fragment: advectionShader,
      uniforms: {
        texelSize,
        dyeTexelSize: { value: new Vec2(1 / dyeResolution) },
        uVelocity: { value: null },
        uSource: { value: null },
        dt: { value: 0.016 },
        dissipation: { value: 1 },
      },
      depthTest: false,
      depthWrite: false,
    }),
  });

  divergenceProgram = new Mesh(gl, {
    geometry: triangle,
    program: new Program(gl, {
      vertex: simVertex,
      fragment: divergenceShader,
      depthTest: false,
      depthWrite: false,
      uniforms: {
        texelSize,
        uVelocity: { value: null },
      },
    }),
  });

  curlProgram = new Mesh(gl, {
    geometry: triangle,
    program: new Program(gl, {
      vertex: simVertex,
      fragment: curlShader,
      depthTest: false,
      depthWrite: false,
      uniforms: {
        texelSize,
        uVelocity: { value: null },
      },
    }),
  });

  vorticityProgram = new Mesh(gl, {
    geometry: triangle,
    program: new Program(gl, {
      vertex: simVertex,
      fragment: vorticityShader,
      depthTest: false,
      depthWrite: false,
      uniforms: {
        texelSize,
        uVelocity: { value: null },
        uCurl: { value: null },
        curl: { value: curlStrength },
        dt: { value: 0.016 },
      },
    }),
  });

  pressureProgram = new Mesh(gl, {
    geometry: triangle,
    program: new Program(gl, {
      vertex: simVertex,
      fragment: pressureShader,
      depthTest: false,
      depthWrite: false,
      uniforms: {
        texelSize,
        uPressure: { value: null },
        uDivergence: { value: null },
      },
    }),
  });

  gradientSubtractProgram = new Mesh(gl, {
    geometry: triangle,
    program: new Program(gl, {
      vertex: simVertex,
      fragment: gradientSubtractShader,
      depthTest: false,
      depthWrite: false,
      uniforms: {
        texelSize,
        uPressure: { value: null },
        uVelocity: { value: null },
      },
    }),
  });
}

/**
 * Resize the canvas. Also updates camera aspect ratio.
 *
 * @param param0 ResizeMessage payload
 */
function resize({ width, height }: ResizeMessage) {
  if (renderer) {
    renderer.setSize(width, height);
    post.resize();
    auroraProgram.uniforms.u_resolution.value = [width, height];
  }
}

/**
 * Render a frame
 */
function render(t: number) {
  if (!renderer || !mesh || !isReady) {
    return;
  }

  const gl = renderer.gl;
  gl.renderer.autoClear = false;

  while (splats.length) {
    const s = splats.pop();
    if (s) {
      splat(s);
    }
  }

  curlProgram.program.uniforms.uVelocity.value = velocity.read.texture;

  gl.renderer.render({
    scene: curlProgram,
    target: curl,
    sort: false,
    update: false,
  });

  vorticityProgram.program.uniforms.uVelocity.value = velocity.read.texture;
  vorticityProgram.program.uniforms.uCurl.value = curl.texture;
  vorticityProgram.program.uniforms.curl.value = curlStrength;

  gl.renderer.render({
    scene: vorticityProgram,
    target: velocity.write,
    sort: false,
    update: false,
  });
  velocity.swap();

  divergenceProgram.program.uniforms.uVelocity.value = velocity.read.texture;

  gl.renderer.render({
    scene: divergenceProgram,
    target: divergence,
    sort: false,
    update: false,
  });

  clearProgram.program.uniforms.uTexture.value = pressure.read.texture;
  clearProgram.program.uniforms.value.value = pressureDissipation;

  gl.renderer.render({
    scene: clearProgram,
    target: pressure.write,
    sort: false,
    update: false,
  });
  pressure.swap();

  pressureProgram.program.uniforms.uDivergence.value = divergence.texture;

  for (let i = 0; i < iterations; i++) {
    pressureProgram.program.uniforms.uPressure.value = pressure.read.texture;

    gl.renderer.render({
      scene: pressureProgram,
      target: pressure.write,
      sort: false,
      update: false,
    });
    pressure.swap();
  }

  gradientSubtractProgram.program.uniforms.uPressure.value =
    pressure.read.texture;
  gradientSubtractProgram.program.uniforms.uVelocity.value =
    velocity.read.texture;

  gl.renderer.render({
    scene: gradientSubtractProgram,
    target: velocity.write,
    sort: false,
    update: false,
  });
  velocity.swap();

  advectionProgram.program.uniforms.dyeTexelSize.value.set(1 / simResolution);
  advectionProgram.program.uniforms.uVelocity.value = velocity.read.texture;
  advectionProgram.program.uniforms.uSource.value = velocity.read.texture;
  advectionProgram.program.uniforms.dissipation.value = velocityDissipation;

  gl.renderer.render({
    scene: advectionProgram,
    target: velocity.write,
    sort: false,
    update: false,
  });
  velocity.swap();

  advectionProgram.program.uniforms.dyeTexelSize.value.set(1 / dyeResolution);
  advectionProgram.program.uniforms.uVelocity.value = velocity.read.texture;
  advectionProgram.program.uniforms.uSource.value = density.read.texture;
  advectionProgram.program.uniforms.dissipation.value = densityDissipation;

  gl.renderer.render({
    scene: advectionProgram,
    target: density.write,
    sort: false,
    update: false,
  });
  density.swap();

  gl.renderer.autoClear = true;

  auroraProgram.uniforms.u_time.value = 10_000 + performance.now() * 0.0003;
  auroraProgram.uniforms.u_num_octaves.value = numOctaves;
  auroraProgram.uniforms.u_random_seed.value = randomSeed;

  pass.uniforms.tFluid.value = density.read.texture;
  pass.uniforms.uTime.value = t * 0.001;

  const revealDuration = 500;
  const revealElapsed = Math.min(t - revealStartTime, revealDuration);

  pass.uniforms.uReveal.value = revealElapsed / revealDuration;

  post.render({ scene: mesh, camera: renderer.camera });

  if (running) {
    frameAnimationToken = requestAnimationFrame(render);
  }
}

/**
 * Start animating
 */
function start() {
  if (running) {
    return;
  }
  stop();
  running = true;
  revealStartTime = performance.now();
  frameAnimationToken = requestAnimationFrame(render);
}

/**
 * Stop animating
 */
function stop() {
  if (!running) {
    return;
  }
  cancelAnimationFrame(frameAnimationToken);
  running = false;
  frameAnimationToken = 0;
}

/**
 * Update mouse coordinates
 */
function mouseMove({ x, y }: MouseMessage) {
  if (!auroraProgram) {
    return;
  }
  auroraProgram.uniforms.u_mouse.value = [x, y];

  if (!prevMouse) {
    prevMouse = new Vec2(x, y);
  }

  const dx = x - prevMouse.x;
  const dy = y - prevMouse.y;

  prevMouse.set(x, y);

  splats.push({
    x: x / (renderer.gl.renderer.width / CANVAS_SCALE_FACTOR),
    y: 1 - (y + 64) / (renderer.gl.renderer.height / CANVAS_SCALE_FACTOR),
    dx: dx * mouseDeltaScaleFactor,
    dy: dy * -mouseDeltaScaleFactor,
  });
}

/**
 * Update the shader parameters
 */
function setShaderParameters(params: ShaderParameters) {
  radius = params.brushSize;
  numOctaves = params.numOctaves;
  randomSeed = params.randomSeed;
  densityDissipation = params.densityDissipation;
  velocityDissipation = params.velocityDissipation;
  pressureDissipation = params.pressureDissipation;
  mouseDeltaScaleFactor = params.mouseDeltaScale;
  curlStrength = params.curlStrength;
}

/**
 * Communication with main thread
 */
self.addEventListener('message', ({ data }: HeroGradientMessage) => {
  switch (data.type) {
    case 'init':
      init(data);
      break;
    case 'resize':
      resize(data);
      break;
    case 'shader-parameters':
      setShaderParameters(data.params);
      break;
    case 'play':
      start();
      break;
    case 'pause':
      stop();
      break;
    case 'mousemove':
      mouseMove(data);
      break;
  }
});
